import {
  AppUser,
  ICategory,
  IComponent,
  IEventrActivity,
  IOptionsWithTypes,
  SectionId,
  spError,
  TItineraryEntry,
  V2,
} from "@eventr/common";
import { ColoursObj, TEventrIcons } from "@eventr/common-storybook";
import { isArray } from "underscore";
import { sectionTranslation } from "../i18n/translateHelpers";
import { getPubItineraryName } from "../state/itineraryState/itineraryMap.slice";
import { ComponentFinder } from "./componentFinder";

type TTagColour =
  | "Dark Red"
  | "Pale Red"
  | "Orange"
  | "Yellow"
  | "Lime"
  | "Bright Green"
  | "Forest Green"
  | "Aqua"
  | "Petrol"
  | "Royal Blue"
  | "Indigo"
  | "Lilac"
  | "Plum"
  | "Magenta"
  | "Pink";

export const publicColour = ColoursObj.tags?.find((c) => c.name === "Indigo")?.hex;

export const getTagColour = (tag: TTagColour) => {
  const tagObj = ColoursObj.tags?.find((c) => c.name === tag);
  if (tagObj) {
    return tagObj.hex;
  }
  return ColoursObj.coreBrights.primary;
};
export const getDataArray = (data: V2): TItineraryEntry[] => {
  const firstKey = Object.keys(data.data)[0];
  return firstKey ? data.data[firstKey] || [] : [];
};

export const getCaptializedName = (userSelected: any) => {
  return (
    userSelected?.firstName.charAt(0).toUpperCase() +
    userSelected?.firstName.slice(1) +
    " " +
    (userSelected?.lastName.charAt(0).toUpperCase() + userSelected?.lastName.slice(1))
  );
};

export const renderComponents = (d: any, components: IComponent[], past?: boolean) => {
  return (
    <>
      {components.map((c, i) => {
        return (
          <ComponentFinder
            key={"finder_" + c.componentKey + i}
            data={d}
            renderOptions={c}
            componentKey={c.componentKey}
            isInPast={past}
          />
        );
      })}
    </>
  );
};

export function getIconName(sectionId: SectionId): TEventrIcons {
  switch (sectionId) {
    case SectionId.GENERAL: {
      return "Info";
    }
    case SectionId.FLIGHT: {
      return "Flight";
    }
    case SectionId.HOTEL: {
      return "Stays";
    }
    case SectionId.HIRE_CAR: {
      return "Hire_Car";
    }
    case SectionId.TRANSPORT: {
      return "Bus";
    }
    case SectionId.ACTIVITY: {
      return "Activity";
    }
    case SectionId.PARKING: {
      return "Parking";
    }
    default:
      return "Activity";
  }
}

export const isUserPartOfUsersWithTags = (wpUser: AppUser, userWithTags: IOptionsWithTypes[]) => {
  try {
    if (userWithTags) {
      return userWithTags.some((u: IOptionsWithTypes) => {
        if (u.type === "User") {
          return u.option === wpUser.id;
        }
        if (u.type === "Department") {
          return wpUser.departmentTags?.some((t) => {
            return t === u.option;
          });
        }
        if (u.type === "Group") {
          return wpUser.groupTags?.some((t) => {
            return t === u.option;
          });
        }
        return false;
      });
    }
    return false;
  } catch (error) {
    spError(error);
    return false;
  }
};

interface IImportedItinerarySection {
  name: string;
  itinCode: string;
  data: V2[];
}
export const splitImportedData = (v2Data?: V2[], categories?: ICategory[]) => {
  const imported: V2[] = [];
  const notImported =
    v2Data?.map((d) => {
      if (d?.sectionID === 8) {
        if (!(d.data?.activities ?? []).length) {
          return d;
        }

        if (isArray(d.data.activities)) {
          const importedActivities: IEventrActivity[] = [];
          const notImported = {
            ...d,
            data: {
              ...d.data,
              activities: d.data.activities.filter((a: IEventrActivity) => {
                if (a?._og_code === undefined) {
                  return true;
                }
                importedActivities.push(a);
                return false;
              }),
              usePage: true,
            },
          };
          if (importedActivities.length) {
            imported.push({
              ...d,
              data: { ...d.data, activities: importedActivities },
            });
          }
          return notImported;
        }
      }
      return d;
    }) ?? [];

  const split: { [key: string]: IEventrActivity[] } = {};
  if (!imported.length) return { split, notImported };
  const firstImported = imported[0]!;
  (firstImported.data?.activities ?? []).forEach((activity: IEventrActivity) => {
    const source: string = activity?._og_code || "";
    if (!source || !split[source]) split[source] = [];
    split[source].push(activity);
  });
  const splitSections: IImportedItinerarySection[] = [];
  for (const key in split) {
    const data = firstImported;
    const activityV2 = {
      ...data,
      sectionNameOverwrite: sectionTranslation("Activities", {
        count: 2,
      }),
      data: { ...data.data, activities: split[key] },
      usePage: true,
      ogCode: key,
    } as V2;
    const categorizedActivities = categoriesActivities([activityV2], categories);

    splitSections.push({
      name: getPubItineraryName(key) ?? "Public Itinerary",
      itinCode: key,
      data: categorizedActivities,
    });
  }

  return { splitSections, notImported };
};

export const splitTodosData = (v2Data?: V2[]) => {
  const todos: any[] = [];
  const allOtherData =
    v2Data?.map((d) => {
      if (d?.sectionID === 8) {
        if (!(d.data?.activities ?? []).length) {
          return d;
        }

        if (isArray(d.data.activities)) {
          d.data.activities = d.data.activities.filter((a: any) => {
            if (a?.type === "todo") {
              // Only extract into the general todos list if it has NO categories.
              // Todos with categories should remain in allOtherData so categoriesActivities
              // can place them under the correct category section.
              if (!a?.categories || !isArray(a.categories) || a.categories.length === 0) {
                todos.push(a);
                return false;
              }
              // Has categories - leave in allOtherData for categoriesActivities to handle
            }
            return true;
          });

          return d;
        }
      }
      return d;
    }) ?? [];
  return { todos, allOtherData };
};

/** A deep copy of a section, with an empty activities list for the caller to fill. */
const copySectionWithoutActivities = (section: V2): V2 =>
  JSON.parse(JSON.stringify({ ...section, data: { ...section.data, activities: [] } }));

export const categoriesActivities = (v2Data?: V2[], categories?: ICategory[]) => {
  if (!v2Data) return [];
  const activitiesSectionIndex = v2Data.findIndex((d) => d.sectionID === 8);
  if (activitiesSectionIndex !== -1) v2Data[activitiesSectionIndex]!.usePage = true;

  if (!categories || categories.length === 0) return v2Data;
  const categoryMap = categories.reduce(
    (acc, cat) => {
      acc[cat.pub_id] = cat;
      return acc;
    },
    {} as { [key: string]: ICategory },
  );

  if (activitiesSectionIndex === -1) {
    return v2Data; // No activities section found
  }

  const originalActivitiesSection = v2Data[activitiesSectionIndex];
  const originalActivities = (originalActivitiesSection?.data?.activities as IEventrActivity[]) ?? [];

  if (!originalActivities.length) {
    return v2Data; // No activities to categorize
  }

  const activitiesByCategoryId: { [key: string]: IEventrActivity[] } = {};
  const uncategorizedActivities: IEventrActivity[] = [];

  originalActivities.forEach((activity) => {
    const activityCategories = activity.categories;
    let categorized = false; // Flag to track if activity was added to any category

    if (isArray(activityCategories) && activityCategories.length > 0) {
      activityCategories.forEach((categoryId) => {
        if (categoryMap[categoryId]) {
          // Check if the category exists in our map
          if (!activitiesByCategoryId[categoryId]) {
            activitiesByCategoryId[categoryId] = [];
          }
          // Avoid adding the same activity multiple times to the same category list if it somehow has duplicate category IDs
          if (!activitiesByCategoryId[categoryId].includes(activity)) {
            activitiesByCategoryId[categoryId].push(activity);
          }
          categorized = true; // Mark as categorized
        }
      });
    }

    // If the activity was not added to any category, add it to uncategorized
    if (!categorized) {
      uncategorizedActivities.push(activity);
    }
  });

  const newSections: V2[] = [];

  // Create a section for uncategorized activities if any exist
  if (uncategorizedActivities.length > 0) {
    const uncategorizedSection = copySectionWithoutActivities(originalActivitiesSection!);
    uncategorizedSection.data = {
      ...uncategorizedSection.data,
      activities: uncategorizedActivities,
    };
    uncategorizedSection.usePage = true;
    newSections.push(uncategorizedSection);
  }

  // Create sections for categorized activities
  const categorizedSections: V2[] = [];

  for (const [categoryIdStr, activities] of Object.entries(activitiesByCategoryId)) {
    const category = categoryMap[categoryIdStr];
    if (!category || activities.length === 0) continue;

    const newSection = copySectionWithoutActivities(originalActivitiesSection!);
    newSection.sectionNameOverwrite = category.name;
    newSection.category = categoryIdStr;
    newSection.iconOverwrite = category.icon;
    newSection.usePage = true;
    newSection.colour = category.colour;
    newSection.data = {
      ...newSection.data,
      activities: activities,
    };
    categorizedSections.push(newSection);
  }

  categorizedSections.sort((a, b) => (a.sectionNameOverwrite ?? "").localeCompare(b.sectionNameOverwrite ?? ""));

  // Combine original data (excluding original activities section) with new sections
  newSections.push(...categorizedSections);

  const result = [
    ...v2Data.slice(0, activitiesSectionIndex),
    ...newSections,
    ...v2Data.slice(activitiesSectionIndex + 1),
  ];
  return result;
};
